import { Psychology } from '@mui/icons-material';

export const LANDING_HEADER_HEIGHT = { xs: 10, sm: 12 } as const;
import {
  AppBar,
  Box,
  Button,
  Container,
  Stack,
  Toolbar,
  Typography,
  useScrollTrigger
} from '@mui/material';
import { HeaderMobileMenu } from '@/components/landing/header-mobile-menu.component';
import { LanguageSwitcher } from '@/components/language-switcher/language-switcher.component';
import { ThemeToggle } from '@/components/theme-toggle.component';
import {
  HOME_PATH,
  PLAN_SELECTION_PATH,
  SIGN_IN_PATH
} from '@/constants/pages';
import { ANALYTICS_EVENTS, captureEvent } from '@/lib/analytics';
import { tDynamic } from '@/lib/i18n';
import { brandVars as brand } from '@/theme/theme';
import Link from 'next/link';
import type { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

// Anchor to the landing page (not bare hashes) so the links resolve from any
// public page the header renders on — on the landing page itself the browser
// still scrolls in place since the path already matches.
const navLinks = [
  { labelKey: 'landing:header.nav.features', href: `${HOME_PATH}#features` },
  {
    labelKey: 'landing:header.nav.howItWorks',
    href: `${HOME_PATH}#how-it-works`
  }
];

export const LandingHeader: FunctionComponent = () => {
  const { t } = useTranslation();

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0
  });

  return (
    <AppBar
      position="fixed"
      elevation={trigger ? 2 : 0}
      sx={(theme) => ({
        bgcolor: trigger ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
        ...(trigger &&
          theme.applyStyles('dark', {
            bgcolor: 'rgba(15, 17, 21, 0.85)'
          })),
        backdropFilter: trigger ? 'blur(8px)' : 'none',
        borderBottom: trigger ? '1px solid' : 'none',
        borderColor: 'divider',
        transition: 'all 0.3s ease'
      })}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ py: 1 }}>
          <Stack
            component={Link}
            href={HOME_PATH}
            sx={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1.5,
              flexGrow: 1,
              textDecoration: 'none'
            }}
          >
            <Box
              sx={{
                background: brand.gradients.logo,
                p: 1,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Psychology sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Typography
              variant="h6"
              sx={{ color: 'text.primary', fontWeight: 700 }}
            >
              FraudFront
            </Typography>
          </Stack>

          <Stack
            sx={{
              flexDirection: 'row',
              gap: 4,
              display: { xs: 'none', md: 'flex' },
              mr: 4
            }}
          >
            {navLinks.map((link) => (
              <Typography
                key={link.labelKey}
                component="a"
                href={link.href}
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  py: 1.5,
                  display: 'inline-flex',
                  alignItems: 'center',
                  '&:hover': {
                    color: 'text.primary'
                  },
                  transition: 'color 0.2s ease'
                }}
              >
                {tDynamic(t, link.labelKey)}
              </Typography>
            ))}
          </Stack>

          <Stack sx={{ flexDirection: 'row', gap: 1, alignItems: 'center' }}>
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 1
              }}
            >
              <LanguageSwitcher />
              <ThemeToggle />
            </Box>
            <Button
              component={Link}
              href={SIGN_IN_PATH}
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                color: 'text.secondary',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  color: 'text.primary',
                  bgcolor: 'transparent'
                }
              }}
            >
              {t('landing:header.signIn')}
            </Button>
            <Button
              component={Link}
              href={PLAN_SELECTION_PATH}
              onClick={() =>
                captureEvent(ANALYTICS_EVENTS.SIGNUP_CTA_CLICKED, {
                  location: 'header'
                })
              }
              variant="contained"
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                textTransform: 'none',
                fontWeight: 500,
                px: 3,
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }}
            >
              {t('landing:header.getStarted')}
            </Button>
            <HeaderMobileMenu
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
              signInLabel={t('landing:header.signIn')}
              navLinks={navLinks.map((link) => ({
                label: tDynamic(t, link.labelKey),
                href: link.href
              }))}
            />
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
  );
};
